import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

export type DateInput = string | number | Date | Dayjs | null | undefined;

/** Узкий неразрывный пробел (U+202F): разделитель разрядов и отбивка символа валюты. */
export const NARROW_NBSP = " ";

const DAY_MONTH = "D MMM";
const DAY_MONTH_YEAR = "D MMM YYYY";
const TIME = "HH:mm";

const toDayjs = (value: DateInput): Dayjs | null => {
  if (value === null || value === undefined || value === "") return null;
  const date = dayjs(value);
  return date.isValid() ? date : null;
};

const daysAgo = (date: Dayjs) => dayjs().startOf("day").diff(date.startOf("day"), "day");
const monthsAgo = (date: Dayjs) => dayjs().startOf("day").diff(date.startOf("day"), "month");
const yearsAgo = (date: Dayjs) => dayjs().startOf("day").diff(date.startOf("day"), "year");

const plural = (count: number, unit: string) => `${count} ${unit}${count === 1 ? "" : "s"}`;

/** Разделитель дат в чатах: Today / Yesterday / 3 Sep / 3 Sep 2026. */
export const formatChatSeparatorDate = (value: DateInput): string => {
  const date = toDayjs(value);
  if (!date) return "";

  const days = daysAgo(date);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  return date.year() === dayjs().year() ? date.format(DAY_MONTH) : date.format(DAY_MONTH_YEAR);
};

/** Дата отзыва: Today / Yesterday / 3 Sep / 1 year ago. */
export const formatReviewDate = (value: DateInput): string => {
  const date = toDayjs(value);
  if (!date) return "";

  const days = daysAgo(date);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";

  const years = yearsAgo(date);
  return years >= 1 ? `${plural(years, "year")} ago` : date.format(DAY_MONTH);
};

/**
 * Дата последнего сообщения: Today, 12:35 / Yesterday, 12:35 / 13 Sep, 12:34 /
 * 1 month ago / Long ago.
 */
export const formatLastMessageDate = (value: DateInput): string => {
  const date = toDayjs(value);
  if (!date) return "";

  const days = daysAgo(date);
  if (days === 0) return `Today, ${date.format(TIME)}`;
  if (days === 1) return `Yesterday, ${date.format(TIME)}`;

  const months = monthsAgo(date);
  if (months < 1) return `${date.format(DAY_MONTH)}, ${date.format(TIME)}`;
  if (months <= 12) return `${plural(months, "month")} ago`;
  return "Long ago";
};

/**
 * Время сообщения в чате: дата по правилу разделителя чата плюс время —
 * Today, 12:35 / Yesterday, 12:35 / 3 Sep, 12:35 / 3 Sep 2026, 12:35.
 */
export const formatMessageDateTime = (value: DateInput): string => {
  const date = toDayjs(value);
  if (!date) return "";

  return `${formatChatSeparatorDate(date)}, ${date.format(TIME)}`;
};

/** Был онлайн: Online Today, 12:35 / Online 13 Sep, 12:34 / Online 1 month ago / Online Long ago. */
export const formatLastOnline = (value: DateInput): string => {
  const lastMessage = formatLastMessageDate(value);
  return lastMessage ? `Online ${lastMessage}` : "";
};

/** Детали сделки: 03.09.2026, 12:35. */
export const formatDealDateTime = (value: DateInput): string => {
  const date = toDayjs(value);
  return date ? date.format("DD.MM.YYYY, HH:mm") : "";
};

/** Дата без времени в интерфейсе сделок/модерации: 03.09.2026. */
export const formatShortDate = (value: DateInput): string => {
  const date = toDayjs(value);
  return date ? date.format("DD.MM.YYYY") : "";
};

/** Транзакции (лог/выписка/документы): 03.09.2026, 12:35:07, UTC+0. */
export const formatTransactionDateTime = (value: DateInput): string => {
  const date = toDayjs(value);
  return date ? `${date.utc().format("DD.MM.YYYY, HH:mm:ss")}, UTC+0` : "";
};

/** Число: 130 929.22 (разряды через U+202F, фиксированная точность). */
export const formatNumber = (value: number | string | null | undefined, fractionDigits = 2): string => {
  const numberValue = Number(value);
  const safeValue = Number.isFinite(numberValue) ? numberValue : 0;

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })
    .format(safeValue)
    .replaceAll(",", NARROW_NBSP);
};

/** Денежная сумма: € 130 929.22 (символ валюты отбит U+202F). */
export const formatMoney = (value: number | string | null | undefined, currency = "€", fractionDigits = 2): string =>
  `${currency}${NARROW_NBSP}${formatNumber(value, fractionDigits)}`;

/** Процент: 12.34%. */
export const formatPercent = (value: number | string | null | undefined, fractionDigits = 2): string =>
  `${formatNumber(value, fractionDigits)}%`;
